import {
  describeSshPublicKey,
  getSshPublicKeyError,
  MAX_SSH_PUBLIC_KEYS,
  MAX_SSH_PUBLIC_KEYS_MESSAGE,
  validateSshPublicKey,
} from '@steerfi/shared';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface SshPublicKeysEditorProps {
  value: string[];
  onChange: (keys: string[]) => void;
  disabled?: boolean;
}

/**
 * Truncates the base64 body of a key for display. Keys are far too long to show
 * in full, and the head and tail together are enough for a human to tell two
 * keys apart.
 */
const previewKeyBody = (key: string): string => {
  const body = key.split(/\s+/)[1] ?? '';
  if (body.length <= 28) return body;
  return `${body.slice(0, 16)}…${body.slice(-8)}`;
};

/**
 * Compares two entries by type and key body only, so the same key re-added with
 * a different comment still counts as a duplicate. Mirrors the de-duplication
 * the API performs on save.
 */
const keyIdentity = (key: string): string => {
  const parts = key.trim().split(/\s+/);
  return parts.length >= 2 ? `${parts[0]} ${parts[1]}` : key.trim();
};

/**
 * Read-only one-line summary of a key: its derived type and size, plus its
 * comment (falling back to a truncated key body when it has none). Used for the
 * non-editing user detail view.
 */
export const SshPublicKeySummary: React.FC<{ sshKey: string }> = ({ sshKey }) => {
  const result = validateSshPublicKey(sshKey);
  return (
    <>
      {result.info ? (
        <Badge variant="secondary">{describeSshPublicKey(result.info)}</Badge>
      ) : (
        <Badge variant="destructive">Invalid</Badge>
      )}
      <span className="font-mono text-xs truncate">
        {result.info?.comment ?? previewKeyBody(sshKey)}
      </span>
    </>
  );
};

/**
 * Add/view/remove editor for a user's authorized SSH public keys.
 *
 * Keys are opaque blobs that are pasted rather than hand-edited, so entries are
 * append-and-remove: existing keys render read-only with their derived type and
 * size, and new keys are validated before being added to the list. The same
 * validation runs again server-side on save.
 */
export const SshPublicKeysEditor: React.FC<SshPublicKeysEditorProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | null>(null);

  const startAdding = () => {
    setDraft('');
    setDraftError(null);
    setIsAdding(true);
  };

  const cancelAdding = () => {
    setDraft('');
    setDraftError(null);
    setIsAdding(false);
  };

  const atLimit = value.length >= MAX_SSH_PUBLIC_KEYS;

  const commitDraft = () => {
    const key = draft.trim().replace(/\s+/g, ' ');

    if (atLimit) {
      setDraftError(MAX_SSH_PUBLIC_KEYS_MESSAGE);
      return;
    }

    const error = getSshPublicKeyError(key);
    if (error) {
      setDraftError(error);
      return;
    }
    if (value.some((existing) => keyIdentity(existing) === keyIdentity(key))) {
      setDraftError('This key has already been added');
      return;
    }

    onChange([...value, key]);
    setDraft('');
    setDraftError(null);
    setIsAdding(false);
  };

  const removeKey = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">SSH Public Keys</Label>

      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No SSH public keys.</p>
      ) : (
        <div className="space-y-1.5">
          {value.map((key, index) => {
            const result = validateSshPublicKey(key);
            const comment = result.info?.comment;
            return (
              <div
                key={keyIdentity(key)}
                className="flex items-start gap-2 p-2 bg-muted/50 rounded-md"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    {result.info ? (
                      <Badge variant="secondary">{describeSshPublicKey(result.info)}</Badge>
                    ) : (
                      <Badge variant="destructive">Invalid</Badge>
                    )}
                    {comment && <span className="text-xs truncate">{comment}</span>}
                  </div>
                  <p className="font-mono text-xs text-muted-foreground truncate">
                    {previewKeyBody(key)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove SSH public key"
                  className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                  disabled={disabled}
                  onClick={() => removeKey(index)}
                >
                  <IconTrash size={13} />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {isAdding ? (
        <div className="space-y-1.5">
          <Textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDraftError(null);
            }}
            placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA... user@host"
            rows={3}
            className="font-mono text-xs"
            disabled={disabled}
            aria-invalid={draftError ? true : undefined}
            aria-label="SSH public key"
          />
          {draftError && <p className="text-xs text-destructive">{draftError}</p>}
          <div className="flex gap-1.5">
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              disabled={disabled || draft.trim() === ''}
              onClick={commitDraft}
            >
              Add
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={disabled}
              onClick={cancelAdding}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1"
            disabled={disabled || atLimit}
            onClick={startAdding}
          >
            <IconPlus size={12} />
            Add SSH key
          </Button>
          {atLimit && <p className="text-xs text-amber-600">{MAX_SSH_PUBLIC_KEYS_MESSAGE}.</p>}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Paste the contents of a public key file (e.g. <code>id_ed25519.pub</code>). Supported: RSA
        (2048 bits or more), ECDSA (nistp256 or better), and ed25519. Keys are optional — SFTP users
        can still authenticate with a password.
      </p>
    </div>
  );
};
